// Thin fetch wrapper around the Foundry backend.
//
// Reads the base URL from `VITE_API_URL` (set per-environment in Vite's
// `.env*` files; defaults to "/api" so a development reverse proxy can
// forward requests to the backend container). Routes through `authFetch`
// so the JWT is attached and a 401 clears the session + dispatches the
// `foundry:session-expired` event the app shell listens for.

import * as Sentry from '@sentry/react';
import type { AppRole } from '@metycle/shared';
import { toast } from 'sonner';

import { API_BASE_URL } from './api-base';
import { authFetch } from './auth-fetch';
import { describeKybTradeGate } from './describe-kyb-trade-gate';
import { formatRoleList } from './role-labels';

const BASE_URL = API_BASE_URL;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// A rejected request body arrives as a flat "Validation failed" with the field
// detail in a separate `errors` array, so reading only `message` leaves the
// operator nothing to act on. That cost ten days of misattributed FX-matching
// failures (#1485) before anyone looked past the flat string.
export function extractApiFieldErrors(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const errors = (body as Record<string, unknown>)['errors'];
  if (!Array.isArray(errors)) return null;
  const details = errors
    .map((issue) => {
      if (issue === null || typeof issue !== 'object') return null;
      const record = issue as Record<string, unknown>;
      const message = record['message'];
      if (typeof message !== 'string' || message.length === 0) return null;
      const path = record['path'];
      const field = Array.isArray(path) ? path.filter((p) => p !== '').join('.') : '';
      return field === '' ? message : `${field}: ${message}`;
    })
    .filter((detail): detail is string => detail !== null);
  return details.length === 0 ? null : details.join('; ');
}

export function extractApiErrorMessage(err: Error, fallback: string): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body !== null) {
    const kybGateMessage = describeKybTradeGate(err.body);
    if (kybGateMessage !== null) return kybGateMessage;
    const fieldErrors = extractApiFieldErrors(err.body);
    if (fieldErrors !== null) return fieldErrors;
    const body = err.body as Record<string, unknown>;
    const msg = body['message'];
    if (typeof msg === 'string') return msg;
    if (Array.isArray(msg) && msg.length > 0 && typeof msg[0] === 'string') return msg[0];
  }
  return fallback;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  // Opt a GET into the 403 permission toast. Mutations always toast; reads
  // stay silent by default (a background page-load query shouldn't pop a
  // context-free toast on navigation). Set this for a read that IS a user
  // action — e.g. a clicked "View / Download" — so its 403 still surfaces.
  toastForbidden?: boolean;
}

type HttpMethod = NonNullable<RequestOptions['method']>;

function extractBodyMessage(parsed: unknown): string | null {
  if (parsed === null || typeof parsed !== 'object') return null;
  const kybGateMessage = describeKybTradeGate(parsed);
  if (kybGateMessage !== null) return kybGateMessage;
  const msg = (parsed as Record<string, unknown>)['message'];
  return typeof msg === 'string' ? msg : null;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const base = path.startsWith('/') ? `${BASE_URL}${path}` : `${BASE_URL}/${path}`;
  if (!query) return base;
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    searchParams.set(key, String(value));
  }
  const qs = searchParams.toString();
  return qs ? `${base}?${qs}` : base;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, options.query);
  const response = await authFetch(url, buildInit(options));
  if (!response.ok) await throwApiError(response, url, options);
  if (response.status === 204) return undefined as T;
  try {
    return (await response.json()) as T;
  } catch {
    // A 2xx whose body isn't JSON means something between us and the API
    // answered instead of the API — e.g. an edge/CDN serving the SPA
    // index.html in place of a real response. Surface it as a typed error
    // rather than letting a raw SyntaxError leak as "unknown".
    throw new ApiError(response.status, null, `Unexpected non-JSON response from ${url}`);
  }
}

// Like `apiRequest` but returns the raw response body as a Blob, for binary
// endpoints (e.g. an inline PDF preview) that don't answer with JSON. Shares
// the auth + typed-error handling: a 401 still clears the session via
// `authFetch`, and a non-2xx still throws a typed `ApiError`.
export async function apiRequestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const url = buildUrl(path, options.query);
  const response = await authFetch(url, buildInit(options));
  if (!response.ok) await throwApiError(response, url, options);
  return response.blob();
}

function extractRequiredRoles(parsed: unknown): AppRole[] | null {
  if (parsed === null || typeof parsed !== 'object') return null;
  const value = (parsed as Record<string, unknown>)['requiredRoles'];
  if (!Array.isArray(value)) return null;
  return value.every((entry) => typeof entry === 'string') ? (value as AppRole[]) : null;
}

function surface403Toast(url: string, parsed: unknown, options: RequestOptions): void {
  const method: HttpMethod = options.method ?? 'GET';
  const kybGateMessage = describeKybTradeGate(parsed);
  if (kybGateMessage !== null) {
    // The KYB trade gate is a specific, actionable block — always surface it,
    // even on a read, because it explains something the user must resolve.
    toast.error(kybGateMessage);
    Sentry.captureMessage('KYB trade gate rejected request', {
      level: 'warning',
      extra: { endpoint: url, body: parsed },
    });
    return;
  }
  const requiredRoles = extractRequiredRoles(parsed);
  Sentry.captureMessage('Forbidden response intercepted on gated endpoint', {
    level: 'warning',
    extra: { endpoint: url, method, requiredRoles: requiredRoles ?? [] },
  });
  // Toast a role denial only for a user-initiated request: any mutation, or a
  // read a caller flagged as a user action (a clicked View / Download). A
  // background page-load GET stays silent so navigation doesn't pop a
  // context-free toast — the query still rejects, so the page can render its
  // own in-context empty / no-access state.
  const isUserAction = method !== 'GET' || options.toastForbidden === true;
  if (!isUserAction) return;
  const message = requiredRoles
    ? `You do not have permission to do this. ${formatRoleList(requiredRoles)} is required — ask an admin to grant access.`
    : 'You do not have permission to do this.';
  toast.error(message);
}

function buildInit(options: RequestOptions): RequestInit {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const isFormData = options.body instanceof FormData;
  // FormData bodies must not carry an explicit Content-Type — the browser
  // sets `multipart/form-data; boundary=...` automatically when fetch sees
  // a FormData body, and JSON.stringify would corrupt the upload.
  if (options.body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';
  if (options.headers) Object.assign(headers, options.headers);
  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.body !== undefined) {
    init.body = isFormData ? (options.body as FormData) : JSON.stringify(options.body);
  }
  if (options.signal) init.signal = options.signal;
  return init;
}

async function throwApiError(
  response: Response,
  url: string,
  options: RequestOptions,
): Promise<never> {
  const text = await response.text();
  let parsed: unknown = text;
  try {
    parsed = text ? JSON.parse(text) : text;
  } catch {
    // Keep the raw text if it isn't JSON.
  }
  if (response.status === 403) surface403Toast(url, parsed, options);
  throw new ApiError(
    response.status,
    parsed,
    extractBodyMessage(parsed) ?? `Request to ${url} failed: ${String(response.status)}`,
  );
}
